import * as React from "react";
import { css, keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { Card } from "main/javascripts/components/card/Card";
import { CardTitle } from "main/javascripts/components/atoms/Card/CardTitle";
import { Hotel } from "main/javascripts/types/hotel";
import { HotelPlan } from "main/javascripts/models/HotelPlan";
import { HotelShowParam } from "main/javascripts/types/hotelShowParam";
import { HotelPlanContent } from "./HotelPlanContent";
import { HotelBedInfo } from "main/javascripts/components/molecules/Hotel/HotelBedInfo";
import { Icon } from "main/javascripts/components/icon/Icon";
import { Image } from "main/javascripts/types/image";
import { defaultHotelRoomImage } from "../../hotelSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import {
  accentColor,
  backgroundColor,
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";

export interface IProps {
  hotelId: number;
  hotelRoom: Hotel.Room;
  param: HotelShowParam;
  onClickImage?: any;
  onClickDetail?: any;
  onSelectHotelPlan?: any;
  loggedInSocialMedia: string;
}

const HotelRoomCardComponent: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const {
    hotelRoom,
    param,
    hotelId,
    onClickDetail,
    onSelectHotelPlan,
    loggedInSocialMedia,
  } = props;
  const { t } = useTranslation(["label"]);

  const image: Image | undefined = defaultHotelRoomImage(hotelRoom);
  const imageStyle: any = image
    ? { backgroundImage: `url("${image ? image.url : ""}")` }
    : {};
  const onClickDetailButton: () => any = (): any => {
    if (hotelRoom.hotelRoomAmenities.length > 0 && onClickDetail) {
      onClickDetail({
        hotelId: hotelId,
        hotelRoom: hotelRoom,
      });
    }
  };
  const detailBlockStyles: any = [detailBlockStyle, clickableStyle];
  return (
    <Card styleKey="cardNoHover">
      <div css={roomBlockStyle}>
        <CardTitle>{props.hotelRoom.name}</CardTitle>
        {image ? (
          <div
            style={imageStyle}
            css={roomImageStyle}
            onClick={onClickDetailButton}
          >
            <div css={imageCountStyle}>
              <span css={imageCountTextStyle}>
                {hotelRoom.hotelRoomImages.length}
              </span>{" "}
              <span css={imageCountIconStyle}>
                <Icon styleKey="images" />
              </span>
            </div>
          </div>
        ) : (
          ""
        )}
        <div css={detailBlockStyles} onClick={onClickDetailButton}>
          <div>
            <HotelBedInfo bedGroups={props.hotelRoom.bedGroups} />
          </div>
          <button css={detailButtonStyle}>
            <span css={detailButtonIconStyle}>
              <Icon styleKey="info" />
            </span>{" "}
            <span css={detailButtonTextStyle}>{t("label:common.detail")}</span>
          </button>
        </div>
      </div>
      <div css={planBlockStyle}>
        {hotelRoom.hotelPlans?.map((hotelPlan: HotelPlan, index: number) => {
          return (
            <HotelPlanContent
              key={index}
              hotelRoomId={hotelRoom.id}
              hotelId={hotelId}
              hotelPlan={hotelPlan}
              param={param}
              onSelectHotelPlan={onSelectHotelPlan}
              loggedInSocialMedia={loggedInSocialMedia}
            />
          );
        })}
        {(!hotelRoom.hotelPlans || hotelRoom.hotelPlans.length === 0) && (
          <div
            css={roomDescriptionStyle}
            dangerouslySetInnerHTML={{
              __html: hotelRoom.description,
            }}
          />
        )}
      </div>
    </Card>
  );
};
export const HotelRoomCard: any = React.memo(HotelRoomCardComponent);

const imageKeyframes = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const roomBlockStyle = css`
  padding: ${space.atom1_5x} ${space.block} 0;
  max-width: ${maxWidth.page1col};
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  ${moreThanBreakpoint("tablet")} {
    background-color: ${backgroundColor.primaryLightColor};
    width: 50%;
    padding-bottom: ${space.atom};
  }
  ${moreThanBreakpoint("desktop")} {
    width: 40%;
  }
`;
const roomImageStyle = css`
  position: relative;
  padding: 56.25% 0 0;
  background-size: cover;
  background-position: center;
  margin: ${space.atom} -${space.atom} 0;
  cursor: pointer;
  animation-name: ${imageKeyframes};
  animation-duration: 0.8s;
  animation-timing-function: ease;
  animation-fill-mode: forwards;
  ${moreThanBreakpoint("tablet")} {
    padding: 62.5% 0 0;
    margin: ${space.atom};
  },
`;
const planBlockStyle = css`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${borderColor.primaryLightColor};
  ${moreThanBreakpoint("tablet")} {
    width: 50%;
    border-top: 0;
    border-left: 1px solid ${borderColor.primaryLightColor};
  }
  ${moreThanBreakpoint("desktop")} {
    width: 60%;
  }
`;
const detailBlockStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${space.atom2x} ${space.atom};
  font-size: ${fontSize.caption};
  color: ${textColor.secondaryDarkColor};
  line-height: 1.5;
  ${moreThanBreakpoint("tablet")} {
    padding: ${space.atom0_5x} ${space.atom};
  }
`;
const detailButtonStyle = css`
  border: 0;
  background: none;
  color: ${accentColor.primaryColor};
  cursor: pointer;
  margin-left: ${space.atom2x};
  padding-left: ${space.atom};
  outline: none;
  font-size: ${fontSize.caption};
  font-weight: ${fontWeight.bold};
  text-align: right;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 1.125rem;
`;
const detailButtonTextStyle = css`
  line-height: ${lineHeight.noSpace};
`;
const detailButtonIconStyle = css`
  font-size: 1.1875rem;
  padding-right: ${space.atom0_5x};
  display: flex;
  align-items: center;
  padding-bottom: 1px;
`;
const imageCountStyle = css`
  position: absolute;
  top: ${space.atom};
  right: ${space.atom};
  background: rgba(0, 0, 0, 0.5);
  padding: 0.25rem 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  border-radius: ${borderRadius.normal};
`;
const imageCountTextStyle = css`
  font-size: ${fontSize.caption};
  padding-right: ${space.atom0_5x};
`;
const imageCountIconStyle = css`
  font-size: 1.1875rem;
`;
const clickableStyle = css`
  cursor: pointer;
`;
const roomDescriptionStyle = css`
  padding: ${space.atom2x} ${space.atom2x};
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.primaryDarkColor};
`;
